import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import {
  fetchHseEvent,
  deleteHseEvent,
  submitHseEvent,
  closeHseEvent,
  exportHseEventPDF,
  createHseAction,
} from "../../../../api/hse/events";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { HseEventForm } from "../../../../components/dashboard/hse/hseEventForm";
import { HseActionForm } from "../../../../components/dashboard/hse/hseActionForm";
import { CustomerTag } from "../../../../components/utils/moduleTags/customerTag";
import { ProjectTag } from "../../../../components/utils/moduleTags/projectTag";
import { EmployeeTag } from "../../../../components/utils/moduleTags/employeeTag";
import { ProjectFolderTag } from "../../../../components/utils/moduleTags/projectFolderTag";
import { Notes } from "../../../../components/dashboard/notes/notes";
import { TinyDocumentSection } from "../../../../components/dashboard/documents/tinyDocumentSection";

/**
 * HSE/KS Event View - View event details (ISO 9001:2015 compliant)
 */
export const HseKsEventView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.includes("/deviations")) return "deviations";
    if (path.includes("/near-misses")) return "near-misses";
    if (path.includes("/accidents")) return "accidents";
    if (path.includes("/complaints")) return "complaints";
    if (path.includes("/observations")) return "observations";
    return "all";
  });

  const tabs = [
    { id: "all", label: "Alle", path: "/dashboard/hse-ks/events" },
    { id: "deviations", label: "Avvik", path: "/dashboard/hse-ks/events/deviations" },
    { id: "near-misses", label: "Nestenulykker", path: "/dashboard/hse-ks/events/near-misses" },
    { id: "accidents", label: "Ulykker", path: "/dashboard/hse-ks/events/accidents" },
    { id: "complaints", label: "Reklamasjoner", path: "/dashboard/hse-ks/events/complaints" },
    { id: "observations", label: "Observasjoner", path: "/dashboard/hse-ks/events/observations" },
  ];

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await fetchHseEvent(id);
      setEvent(response.data.data);
    } catch (err) {
      console.error("Error fetching HSE event:", err);
      setError(true);
      toast.error(t("Kunne ikke laste hendelse"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const handleDelete = async () => {
    if (
      window.confirm(t("Er du sikker på at du vil slette denne hendelsen?"))
    ) {
      try {
        await deleteHseEvent(id);
        toast.success(t("Hendelse ble slettet"));
        navigate("/dashboard/hse-ks/events");
      } catch (err) {
        toast.error(t("Kunne ikke slette hendelse"));
      }
    }
  };

  const handleSubmit = async () => {
    try {
      await submitHseEvent(id);
      toast.success(t("Hendelse ble innsendt"));
      fetchEvent();
    } catch (err) {
      toast.error(t("Kunne ikke sende hendelse"));
    }
  };

  const handleClose = async () => {
    try {
      await closeHseEvent(id);
      toast.success(t("Hendelse ble lukket"));
      fetchEvent();
    } catch (err) {
      toast.error(t("Kunne ikke lukke hendelse"));
    }
  };

  const handleExportPDF = async () => {
    if (isExportingPDF) return;
    if (!event?.id) {
      toast.error("Hendelsen må være lagret før PDF kan eksporteres");
      return;
    }
    setIsExportingPDF(true);
    try {
      const result = await exportHseEventPDF(event.id);
      if (result.data) {
        let blob;
        if (result.data instanceof Blob) {
          blob = result.data;
        } else {
          blob = new Blob([result.data], { type: 'application/pdf' });
        }
        if (blob.size < 100) {
          throw new Error("PDF-filen er for liten eller ugyldig");
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `hms_hendelse_${event.id}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("PDF ble eksportert");
      } else {
        const errorMsg = result.errors?.join(", ") || "Kunne ikke eksportere PDF";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(error.message || "Kunne ikke eksportere PDF");
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleCreateAction = async (actionData) => {
    try {
      setActionLoading(true);
      await createHseAction({ ...actionData, hse_event_id: event.id });
      toast.success(t("Tiltak ble opprettet"));
      setShowActionDialog(false);
      fetchEvent();
    } catch (err) {
      console.error("Error creating HSE action:", err);
      toast.error(t("Kunne ikke opprette tiltak"));
    } finally {
      setActionLoading(false);
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case "injury":
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      case "near_miss":
        return <ClockIcon className="h-6 w-6 text-yellow-500" />;
      case "property_damage":
        return <XCircleIcon className="h-6 w-6 text-orange-500" />;
      case "environmental":
        return <ExclamationTriangleIcon className="h-6 w-6 text-green-500" />;
      default:
        return <ExclamationTriangleIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case "draft":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("Kladd")}
          </span>
        );
      case "submitted":
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            {t("Innsendt")}
          </span>
        );
      case "under_review":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("Under vurdering")}
          </span>
        );
      case "closed":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("Lukket")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getSeverityBadge = (severity) => {
    const baseClasses =
      "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium";
    switch (severity) {
      case "low":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("Lav")}
          </span>
        );
      case "medium":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("Medium")}
          </span>
        );
      case "high":
        return (
          <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
            {t("Høy")}
          </span>
        );
      case "critical":
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            {t("Kritisk")}
          </span>
        );
      default:
        return null;
    }
  };

  const headerActions = [
    {
      component: null,
      label: isExportingPDF ? "Eksporterer..." : "Eksporter PDF",
      description: "",
      path: null,
      action: handleExportPDF,
      icon: DocumentArrowDownIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
      disabled: isExportingPDF,
    },
    {
      component: null,
      label: t("Slett"),
      action: handleDelete,
      icon: TrashIcon,
      textColor: "text-red-700",
      bgColor: "bg-white",
      hidden: !event?.can_be_edited,
      menu: true,
    },
  ];

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste hendelse")]} />;
  }

  if (!event) {
    return <ResourceEmpty />;
  }

  return (
    <>
      <DashboardHeader
        title={event.title}
        subtitle="Hendelsesdetaljer (ISO 9001:2015 - kap. 10 Forbedring)"
        actions={headerActions}
        mainButton={{
          label: t("Rediger"),
          action: () => setShowEditDialog(true),
          path: null,
          color: "",
          icon: PencilIcon,
          hidden: !event.can_be_edited,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            {/* Tabs */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
                <nav className="flex space-x-4" aria-label="Tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        navigate(tab.path);
                      }}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Back Button */}
            <div className="bg-white shadow sm:rounded-lg px-4 py-5 sm:p-6">
              <Link
                to="/dashboard/hse-ks/events"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t("Tilbake til hendelser")}
              </Link>
            </div>

            {/* Event Header */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getEventTypeIcon(event.event_type)}
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {event.title}
                      </h3>
                      <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        {event.event_type_display} •{" "}
                        {new Date(event.event_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(event.status)}
                    {getSeverityBadge(event.severity_level)}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Basic Information */}
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t("Grunnleggende informasjon")}
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Beskrivelse")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                          {event.description || t("Ingen beskrivelse")}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Sted")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {event.location}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Avdeling")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {event.department?.displayTitle || t("Ingen avdeling")}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Bygg/Område")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {event.building || t("Ingen bygg/område")}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Rom/Område")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {event.room_area || t("Ingen rom/område")}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Detaljert beskrivelse av stedet")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                          {event.location_details || t("Ingen detaljer")}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* People Involved */}
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t("Involverte personer")}
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Involverte personer")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {event.involved_employees &&
                          event.involved_employees.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {event.involved_employees.map((employee, index) => (
                                <span
                                  key={employee.id || index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {employee.displayTitle ||
                                    employee.title ||
                                    `Bruker ${employee.id}`}
                                </span>
                              ))}
                            </div>
                          ) : (
                            t("Ingen")
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Ansvarlig person")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {event.responsible_user?.displayTitle ||
                            event.responsible_user?.title ||
                            t("Ingen")}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Vitner")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {event.witnesses || t("Ingen")}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Analysis and Actions */}
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t("Analyse og tiltak")}
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6">
                      {event.root_cause_analysis_required && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            {t("Årsakanalyse")}
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                            {event.root_cause_analysis_details ||
                              t("Ingen detaljer")}
                          </dd>
                        </div>
                      )}
                      {event.immediate_actions_required && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            {t("Strakstiltak")}
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                            {event.immediate_actions_details ||
                              t("Ingen detaljer")}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t("Forslag til tiltak")}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                          {event.suggested_actions || t("Ingen forslag")}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Documents */}
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t("Dokumenter")}
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <TinyDocumentSection
                      documents={event.documents}
                      module="hse_event"
                      module_param={event.id}
                    />
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Related Entities */}
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t("Tilknyttede elementer")}
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <div className="space-y-4">
                      <CustomerTag
                        customer={event.customer}
                        onRefresh={() => {}}
                      />
                      <ProjectTag project={event.project} onRefresh={() => {}} />
                      <EmployeeTag
                        employee={event.responsible_user}
                        label={t("Ansvarlig")}
                        onRefresh={() => {}}
                      />
                      <ProjectFolderTag
                        folder={event.folder}
                        label={t("Mappe")}
                        onRefresh={() => {}}
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t("Notater")}
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <Notes module="hse_event" module_param={event.id} />
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t("Handlinger")}
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <div className="space-y-3">
                      {event.status === "draft" && (
                        <button
                          onClick={handleSubmit}
                          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          {t("Send inn")}
                        </button>
                      )}
                      {(event.status === "submitted" ||
                        event.status === "under_review") && (
                        <button
                          onClick={handleClose}
                          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          {t("Lukk hendelse")}
                        </button>
                      )}
                      <button
                        onClick={() => setShowActionDialog(true)}
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        {t("Opprett tiltak")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardHeader>

      {/* Edit Dialog */}
      <PMDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        title={t("Rediger hendelse")}
        size="6xl"
      >
        <HseEventForm
          event={event}
          onSubmit={(updatedEvent) => {
            setEvent(updatedEvent);
            setShowEditDialog(false);
            toast.success(t("Hendelse ble oppdatert"));
            fetchEvent();
          }}
          onCancel={() => setShowEditDialog(false)}
        />
      </PMDialog>

      {/* Create Action Dialog */}
      <PMDialog
        open={showActionDialog}
        onClose={() => setShowActionDialog(false)}
        title={t("Opprett tiltak")}
        size="4xl"
      >
        <HseActionForm
          hseEvent={event}
          onSubmit={handleCreateAction}
          onCancel={() => setShowActionDialog(false)}
          loading={actionLoading}
        />
      </PMDialog>
    </>
  );
};

